/**
 * Locale resource bundles for i18next.
 * Each AppLocale maps to the same set of namespaces.
 */
import type { AppLocale } from './locales';

import enCommon from '@/locales/en/common.json';
import enAuth from '@/locales/en/auth.json';
import enConsent from '@/locales/en/consent.json';
import enPets from '@/locales/en/pets.json';
import enLanguage from '@/locales/en/language.json';
import enInvite from '@/locales/en/invite.json';
import enNav from '@/locales/en/nav.json';
import enProfile from '@/locales/en/profile.json';
import enLabels from '@/locales/en/labels.json';
import enHelp from '@/locales/en/help.json';
import enFaqs from '@/locales/en/faqs.json';
import enDocs from '@/locales/en/docs.json';
import enTours from '@/locales/en/tours.json';
import enTips from '@/locales/en/tips.json';
import enOverview from '@/locales/en/overview.json';
import enBilling from '@/locales/en/billing.json';
import enPedigree from '@/locales/en/pedigree.json';
import enScreens from '@/locales/en/screens.json';
import enForms from '@/locales/en/forms.json';

import esARCommon from '@/locales/es-AR/common.json';
import esARAuth from '@/locales/es-AR/auth.json';
import esARConsent from '@/locales/es-AR/consent.json';
import esARPets from '@/locales/es-AR/pets.json';
import esARLanguage from '@/locales/es-AR/language.json';
import esARInvite from '@/locales/es-AR/invite.json';
import esARNav from '@/locales/es-AR/nav.json';
import esARProfile from '@/locales/es-AR/profile.json';
import esARLabels from '@/locales/es-AR/labels.json';
import esARHelp from '@/locales/es-AR/help.json';
import esARFaqs from '@/locales/es-AR/faqs.json';
import esARDocs from '@/locales/es-AR/docs.json';
import esARTours from '@/locales/es-AR/tours.json';
import esARTips from '@/locales/es-AR/tips.json';
import esARScreens from '@/locales/es-AR/screens.json';
import esARForms from '@/locales/es-AR/forms.json';
import esAROverview from '@/locales/es-AR/overview.json';
import esARBilling from '@/locales/es-AR/billing.json';
import esARPedigree from '@/locales/es-AR/pedigree.json';

import es419Common from '@/locales/es-419/common.json';
import es419Auth from '@/locales/es-419/auth.json';
import es419Consent from '@/locales/es-419/consent.json';
import es419Pets from '@/locales/es-419/pets.json';
import es419Language from '@/locales/es-419/language.json';
import es419Invite from '@/locales/es-419/invite.json';
import es419Nav from '@/locales/es-419/nav.json';
import es419Profile from '@/locales/es-419/profile.json';
import es419Labels from '@/locales/es-419/labels.json';
import es419Help from '@/locales/es-419/help.json';
import es419Faqs from '@/locales/es-419/faqs.json';
import es419Docs from '@/locales/es-419/docs.json';
import es419Tours from '@/locales/es-419/tours.json';
import es419Tips from '@/locales/es-419/tips.json';
import es419Screens from '@/locales/es-419/screens.json';
import es419Forms from '@/locales/es-419/forms.json';
import es419Overview from '@/locales/es-419/overview.json';
import es419Billing from '@/locales/es-419/billing.json';
import es419Pedigree from '@/locales/es-419/pedigree.json';

import esESCommon from '@/locales/es-ES/common.json';
import esESAuth from '@/locales/es-ES/auth.json';
import esESConsent from '@/locales/es-ES/consent.json';
import esESPets from '@/locales/es-ES/pets.json';
import esESLanguage from '@/locales/es-ES/language.json';
import esESInvite from '@/locales/es-ES/invite.json';
import esESNav from '@/locales/es-ES/nav.json';
import esESProfile from '@/locales/es-ES/profile.json';
import esESLabels from '@/locales/es-ES/labels.json';
import esESHelp from '@/locales/es-ES/help.json';
import esESFaqs from '@/locales/es-ES/faqs.json';
import esESDocs from '@/locales/es-ES/docs.json';
import esESTours from '@/locales/es-ES/tours.json';
import esESTips from '@/locales/es-ES/tips.json';
import esESScreens from '@/locales/es-ES/screens.json';
import esESForms from '@/locales/es-ES/forms.json';
import esESOverview from '@/locales/es-ES/overview.json';
import esESBilling from '@/locales/es-ES/billing.json';
import esESPedigree from '@/locales/es-ES/pedigree.json';

import itCommon from '@/locales/it/common.json';
import itAuth from '@/locales/it/auth.json';
import itConsent from '@/locales/it/consent.json';
import itPets from '@/locales/it/pets.json';
import itLanguage from '@/locales/it/language.json';
import itInvite from '@/locales/it/invite.json';
import itNav from '@/locales/it/nav.json';
import itProfile from '@/locales/it/profile.json';
import itLabels from '@/locales/it/labels.json';
import itHelp from '@/locales/it/help.json';
import itFaqs from '@/locales/it/faqs.json';
import itDocs from '@/locales/it/docs.json';
import itTours from '@/locales/it/tours.json';
import itTips from '@/locales/it/tips.json';
import itScreens from '@/locales/it/screens.json';
import itForms from '@/locales/it/forms.json';
import itOverview from '@/locales/it/overview.json';
import itBilling from '@/locales/it/billing.json';
import itPedigree from '@/locales/it/pedigree.json';

export const I18N_NAMESPACES = [
  'common',
  'auth',
  'consent',
  'pets',
  'language',
  'invite',
  'nav',
  'profile',
  'labels',
  'help',
  'faqs',
  'docs',
  'tours',
  'tips',
  'screens',
  'overview',
  'billing',
  'pedigree',
  'forms'
] as const;

export type I18nNamespace = (typeof I18N_NAMESPACES)[number];

type ResourceBundle = Record<I18nNamespace, object>;

const en: ResourceBundle = {
  common: enCommon,
  auth: enAuth,
  consent: enConsent,
  pets: enPets,
  language: enLanguage,
  invite: enInvite,
  nav: enNav,
  profile: enProfile,
  labels: enLabels,
  help: enHelp,
  faqs: enFaqs,
  docs: enDocs,
  tours: enTours,
  tips: enTips,
  screens: enScreens,
  overview: enOverview,
  billing: enBilling,
  pedigree: enPedigree,
  forms: enForms
};

const esAR: ResourceBundle = {
  common: esARCommon,
  auth: esARAuth,
  consent: esARConsent,
  pets: esARPets,
  language: esARLanguage,
  invite: esARInvite,
  nav: esARNav,
  profile: esARProfile,
  labels: esARLabels,
  help: esARHelp,
  faqs: esARFaqs,
  docs: esARDocs,
  tours: esARTours,
  tips: esARTips,
  screens: esARScreens,
  overview: esAROverview,
  billing: esARBilling,
  pedigree: esARPedigree,
  forms: esARForms
};

const es419: ResourceBundle = {
  common: es419Common,
  auth: es419Auth,
  consent: es419Consent,
  pets: es419Pets,
  language: es419Language,
  invite: es419Invite,
  nav: es419Nav,
  profile: es419Profile,
  labels: es419Labels,
  help: es419Help,
  faqs: es419Faqs,
  docs: es419Docs,
  tours: es419Tours,
  tips: es419Tips,
  screens: es419Screens,
  overview: es419Overview,
  billing: es419Billing,
  pedigree: es419Pedigree,
  forms: es419Forms
};

const esES: ResourceBundle = {
  common: esESCommon,
  auth: esESAuth,
  consent: esESConsent,
  pets: esESPets,
  language: esESLanguage,
  invite: esESInvite,
  nav: esESNav,
  profile: esESProfile,
  labels: esESLabels,
  help: esESHelp,
  faqs: esESFaqs,
  docs: esESDocs,
  tours: esESTours,
  tips: esESTips,
  screens: esESScreens,
  overview: esESOverview,
  billing: esESBilling,
  pedigree: esESPedigree,
  forms: esESForms
};

const it: ResourceBundle = {
  common: itCommon,
  auth: itAuth,
  consent: itConsent,
  pets: itPets,
  language: itLanguage,
  invite: itInvite,
  nav: itNav,
  profile: itProfile,
  labels: itLabels,
  help: itHelp,
  faqs: itFaqs,
  docs: itDocs,
  tours: itTours,
  tips: itTips,
  screens: itScreens,
  overview: itOverview,
  billing: itBilling,
  pedigree: itPedigree,
  forms: itForms
};

export const localeResources: Record<AppLocale, ResourceBundle> = {
  en,
  'es-AR': esAR,
  'es-419': es419,
  'es-ES': esES,
  it
};
